import Navigo from "navigo";
import Clarity from '@microsoft/clarity';
import { isAuthenticated, isAuthenticatedToExploreApp } from './lib/utility';
import { closeDDOnNavigoClick } from "./lib/lib.ts";

import { AppLayout } from "../views/app-layout.ts";
import { BlankLayout } from "../views/blank-layout.ts";
import { useTheme } from "./utils/useTheme.ts";
import { HSStaticMethods } from "preline";
import { cleanUpAllChildren, initAllComponents } from "./lib/initAllComponents.ts";
import { Component } from "../types.ts";
import { SelectedWorkspaceCtx } from "./contexts/SelectedWorkspaceCtx.ts";
import { userDetailStore } from "./stores/userDetailStore.ts";
import { userDetailService } from "./services/userDetailService.ts";

const router = new Navigo("/");

export const $layoutContainer = document.querySelector("#layout-container") as HTMLElement;

export const appLayout = AppLayout();
export const blankLayoutComp = BlankLayout();

let currPage: Component | null = null;
let prevPage: Component | null = null;

// load the html view
const loadPageHtml = async (path: string): Promise<string> => {
  try {
    const response = await fetch(`/views/${path}.html`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const content = await response.text();
    return content;
  } catch (error) {
    console.error("Error loading page:", error);
    return "<p>Error loading page content.</p>";
  }
};

// render the page html into app
// we have Two layouts: default and blank
// default layout is the main layout with the sidebar and header
// blank layout is the layout without the sidebar and header
type layoutTypes = "default" | "blank";
let currentLayout: layoutTypes;
let profileChannel: BroadcastChannel | null = null;

const renderPageHtml = (
  content: string,
  layout: layoutTypes = "default"
) => {
  if (layout === "blank") {
    if (currentLayout !== "blank") {
      cleanUpAllChildren(appLayout); // clean up app layout components
      $layoutContainer.innerHTML = blankLayoutComp.render();
      initAllComponents(blankLayoutComp);
    }
    currentLayout = "blank";
    blankLayoutComp.populate(content);
  } else if (layout === "default") {
    const isAbleToExploreApp = isAuthenticatedToExploreApp();
    if (!isAbleToExploreApp) {
      router.navigate("/onboarding");
      return false;
    }

    if (!SelectedWorkspaceCtx.isWorkspaceCookiePresent()) {
      router.navigate("/workspaces");
      return false;
    }
    if (currentLayout !== "default") {
      cleanUpAllChildren(blankLayoutComp); // clean up blank layout components
      $layoutContainer.innerHTML = appLayout.render();
      initAllComponents(appLayout);
    }
    currentLayout = "default";
    appLayout.reinit();
    appLayout.populate(content);

    if (profileChannel) {
      profileChannel.close();
    }
    profileChannel = new BroadcastChannel('profile_updates');
    profileChannel.onmessage = (event) => {
      userDetailStore.setUserDetail(event.data);
    };
  } else {
    console.error("Invalid layout passed!");
    return false;
  }

  HSStaticMethods.autoInit(); // Initialize Preline components after the page is loaded
  router.updatePageLinks(); // Initialize data-navigo page links if any in the new DOM of the page
  closeDDOnNavigoClick();

  return true;
};

// if the script module has a default export, execute it
const initPageScript = async (module: any) => {
  if (typeof module.default === "function") {
    module.default();
  }
};

// List of public routes that do not require authentication
const publicRoutes = [
  'login',
  'signup',
  'forgot-password',
  'reset-password'
];

router.hooks({
  before: (done, match) => {
    document.title = import.meta.env.VITE_DEFAULT_TITLE + import.meta.env.VITE_TITLE_APPEND_TEXT;
    const isAuth = isAuthenticated();
    const isPublicRoute = publicRoutes.includes(match?.url || '');

    // If user is authenticated and accessing a public route, redirect to home
    if (isAuth && ['login', 'signup'].includes(match?.url || '')) {
      window.location.href = '/';
      return;
    }

    // If user is not authenticated and accessing a protected route, redirect to login
    if (!isAuth && !isPublicRoute) {
      window.location.href = '/login';
      return;
    }

    if (isPublicRoute)
      useTheme("light"); // use the light theme for public routes

    done(); // Continue to the requested route
  },
  leave: (done) => {
    if (currPage) {
      cleanUpAllChildren(currPage);
      prevPage = currPage;
      currPage = null;
    }
    done();
  }
});

router
  .on(
    "/",
    async () => {
      router.navigate("/feedbox");
    },
    {
      before(done) {
        done();
      },
    }
  )
  .on("/signup", async () => {
    const pageHtml = await loadPageHtml("signup");
    if(!renderPageHtml(pageHtml, "blank")) return;
    const module = await import("../views/signup.ts");
    initPageScript(module);
  })
  .on("/login", async () => {
    const pageHtml = await loadPageHtml("login");
    if(!renderPageHtml(pageHtml, "blank")) return;
    const module = await import("../views/login.ts");
    initPageScript(module);
  })
  .on("/forgot-password", async () => {
    const pageHtml = await loadPageHtml("forgot-password");
    if(!renderPageHtml(pageHtml, "blank")) return;
    const module = await import("../views/forgot-password.ts");
    initPageScript(module);
  })
  .on("/reset-password", async () => {
    const pageHtml = await loadPageHtml("reset-password");
    if(!renderPageHtml(pageHtml, "blank")) return;
    const module = await import("../views/reset-password.ts");
    initPageScript(module);
  })
  .on("/feedbox", async () => {
    document.title = `Feedbox ${import.meta.env.VITE_TITLE_APPEND_TEXT}`;
    const { Feedbox } = await import("../views/feedbox.ts");
    const feedboxPage = Feedbox();
    const pageHtml = feedboxPage.render();
    if(!renderPageHtml(pageHtml)) return;
    initAllComponents(feedboxPage);
    currPage = feedboxPage;
  })
  .on("/workspaces/settings", async () => {
    document.title = `Workspace Setting ${import.meta.env.VITE_TITLE_APPEND_TEXT}`;
    const { WorkspaceSettings } = await import("../views/workspace-settings.ts");
    const wsSettingsPage = WorkspaceSettings();
    const pageHtml = wsSettingsPage.render();
    if(!renderPageHtml(pageHtml)) return;
    initAllComponents(wsSettingsPage);
    currPage = wsSettingsPage;
  })
  .on("/workspaces", async () => {

    const isAbleToExploreApp = isAuthenticatedToExploreApp();
    if (!isAbleToExploreApp) {
      router.navigate("/onboarding");
      return;
    }
    document.title = `Workspaces ${import.meta.env.VITE_TITLE_APPEND_TEXT}`;
    const { Workspaces } = await import("../views/workspaces.ts");
    const workspacesPage = Workspaces();
    const pageHtml = workspacesPage.render();
    if(!renderPageHtml(pageHtml, "blank")) return;
    initAllComponents(workspacesPage);
    currPage = workspacesPage;
  })
  .on("/forms", async () => {
    const { Forms } = await import("../views/forms.ts");
    const formsPage = Forms();
    const pageHtml = formsPage.render();
    if(!renderPageHtml(pageHtml)) return;
    initAllComponents(formsPage);
    currPage = formsPage;
  }).on("/forms/:id/share/:action", async (data) => {
    const lastResolvedMatch = router.lastResolved();
    if (lastResolvedMatch && (lastResolvedMatch[0].route.path === "forms/:id/share/:action" || lastResolvedMatch[0].route.path === "forms")) {
      const isFromForm = lastResolvedMatch[0].route.path === "forms";
      if(isFromForm){
        currPage = prevPage;
      }
      if (currPage?.populate) currPage?.populate(data?.data);
    } else {
      const { Forms } = await import("../views/forms.ts");
      const formsPage = Forms(data?.data || { id: null, action: null });
      const pageHtml = formsPage.render();
      if(!renderPageHtml(pageHtml)) return;
      initAllComponents(formsPage);
      currPage = formsPage;
    }
  }).on("/forms/:id/edit", async (data) => {
    let editData = data?.data || { id: null, action: null };
    editData.action = "edit";
    const { Forms } = await import("../views/forms.ts");
    const formsPage = Forms(editData);
    const pageHtml = formsPage.render();
    if(!renderPageHtml(pageHtml)) return;
    initAllComponents(formsPage);
    currPage = formsPage;
  })
  .on("/pages", async () => {
    const { FeedPages } = await import("../views/pages.ts");
    const feedPagesPage = FeedPages();
    const pageHtml = feedPagesPage.render();
    if(!renderPageHtml(pageHtml)) return;
    initAllComponents(feedPagesPage);
    currPage = feedPagesPage;
  })
  .on("/pages/:id/share/:action", async (data) => {
    const lastResolvedMatch = router.lastResolved();
    if (lastResolvedMatch && (lastResolvedMatch[0].route.path === "pages/:id/share/:action" || lastResolvedMatch[0].route.path === "pages")) {
      const isFromPage = lastResolvedMatch[0].route.path === "pages";
      if(isFromPage){
        currPage = prevPage;
      }
      if (currPage?.populate) currPage?.populate(data?.data);
    } else {
      const { FeedPages } = await import("../views/pages.ts");
      const feedPagesPage = FeedPages(data?.data || { id: null, action: null });
      const pageHtml = feedPagesPage.render();
      if(!renderPageHtml(pageHtml)) return;
      initAllComponents(feedPagesPage);
      currPage = feedPagesPage;
    }
  })
  .on("/pages/:id/edit", async (data) => {
    const { FeedPages } = await import("../views/pages.ts");
    const feedPagesPage = FeedPages(data?.data || { id: null, action: null });
    const pageHtml = feedPagesPage.render();
    if(!renderPageHtml(pageHtml)) return;
    initAllComponents(feedPagesPage);
    currPage = feedPagesPage;
  })
  .on("/domain", async () => {
    const { CustomDomain } = await import("../views/custom-domain.ts");
    const CustomDomainPage = CustomDomain();
    const customDomainHtml = CustomDomainPage.render();
    if(!renderPageHtml(customDomainHtml)) return;
    initAllComponents(CustomDomainPage);
    currPage = CustomDomainPage;
  })
  .on("/onboarding", async () => {
    await userDetailService.fetchDetail();
    const isAbleToExploreApp = isAuthenticatedToExploreApp();
    if (isAbleToExploreApp) {
      router.navigate("/feedbox"); // Redirect to the feedbox if the user has workspaces
      return;
    }
    const { Onboarding } = await import("../views/onboarding.ts");
    const PageComponent = Onboarding();
    const pageHtml = PageComponent.render();
    if(!renderPageHtml(pageHtml, "blank")) return;
    initAllComponents(PageComponent);
    currPage = PageComponent;

    // Send event to Clarity
    Clarity.event("Onboarding");
  })
  .on("/widgets", async () => {
    document.title = `Embed Widgets ${import.meta.env.VITE_TITLE_APPEND_TEXT}`;
    const { Widget } = await import("../views/widgets.ts");
    const WidgetPage = Widget();
    const customDomainHtml = WidgetPage.render();
    if(!renderPageHtml(customDomainHtml)) return;
    initAllComponents(WidgetPage);
    currPage = WidgetPage;
  });

router.notFound(async () => {
  router.navigate("/");
});

export { router };
